import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import connectRouter from 'components/Router/connect'
import { connect as connectStaticText } from 'components/StaticText/connect'
import ImmutablePropTypes from 'react-immutable-proptypes'
import Link from 'components/Link'
import TileHero from 'components/TileHero'
import Vote, { SIZE_SMALL as VOTE_SIZE_SMALL } from 'components/Vote'
import ProgressBar from 'components/ProgressBar'
import { formatDuration } from 'theme/web-app'
import {
  TYPE_CONTENT_VIDEO,
  TYPE_CONTENT_VIDEO_YOGA,
  TYPE_CONTENT_VIDEO_FITNESS,
  TYPE_CONTENT_VIDEO_MEDITATION,
} from 'services/content-type'
import { WATCH_NODE_TYPE_FEATURE } from 'components/Watch'
import WatchAccess, {
  ACCESS_CHECK_SHARE_FEATURE_GEO,
  ACCESS_CHECK_AUTH_FEATURE_GEO,
  ACCESS_FEATURE,
  ACCESS_PREVIEW,
} from 'components/WatchAccess'
import WatchAccessAllowed from 'components/WatchAccessAllowed'
import WatchAccessDenied from 'components/WatchAccessDenied'
import NotAvailable, {
  TYPE_NOT_AVAILABLE_OVERLAY,
} from 'components/NotAvailable'
import { getBoundActions } from 'actions'
import {
  upstreamContextOnClick,
} from 'services/upstream-context'
import { CLICK_TO_DETAILS_PAGE_GA4_EVENT } from 'services/event-tracking'
import { fromJS } from 'immutable'

function TileVideoMetaBottomMetaItem (props) {
  const { type, path } = props
  if (path) {
    return (
      <Link to={path}>{type}</Link>
    )
  }
  return type
}

function renderLabel (value) {
  if (value) {
    return <div className="tile-video__label">{value}</div>
  }
  return null
}

function renderTileProgressBar (userInfo, duration) {
  if (!userInfo || !duration || !userInfo.get('featurePosition')) {
    return null
  }
  return (
    <div className="tile-video__progress-bar">
      <ProgressBar duration={duration} userInfo={userInfo} />
    </div>
  )
}

function TileVideoMetaBottom (props) {
  const meta = []
  const metaClassName = 'tile-video__meta-item'
  const {
    yogaLevelPath,
    yogaStylePath,
    yogaDurationPath,
    fitnessLevelPath,
    fitnessStylePath,
    fitnessDurationPath,
  } = props
  switch (props.type) {
    case TYPE_CONTENT_VIDEO_YOGA:
      if (props.yogaStyle) {
        meta.push(
          <span
            className={metaClassName}
            key={`tile-video-yoga-style-${props.id}`}
          >
            <TileVideoMetaBottomMetaItem type={props.yogaStyle} path={yogaStylePath} />
          </span>,
        )
      }
      if (props.yogaLevel) {
        meta.push(
          <span
            className={metaClassName}
            key={`tile-video-yoga-level-${props.id}`}
          >
            <TileVideoMetaBottomMetaItem type={props.yogaLevel} path={yogaLevelPath} />
          </span>,
        )
      }
      break

    case TYPE_CONTENT_VIDEO_FITNESS:
      if (props.fitnessStyle) {
        meta.push(
          <span
            className={metaClassName}
            key={`tile-video-fitness-style-${props.id}`}
          >
            <TileVideoMetaBottomMetaItem type={props.fitnessStyle} path={fitnessStylePath} />
          </span>,
        )
      }
      if (props.fitnessLevel) {
        meta.push(
          <span
            className={metaClassName}
            key={`tile-video-fitness-level-${props.id}`}
          >
            <TileVideoMetaBottomMetaItem type={props.fitnessLevel} path={fitnessLevelPath} />
          </span>,
        )
      }
      break

    case TYPE_CONTENT_VIDEO_MEDITATION:
      if (props.meditationStyle) {
        meta.push(
          <span
            className={metaClassName}
            key={`tile-video-meditation-style-${props.id}`}
          >
            {props.meditationStyle}
          </span>,
        )
      }
      break
    default:
      break
  }

  if (props.duration) {
    meta.push(
      <span
        className={`${metaClassName} ${metaClassName}--last`}
        key={`tile-video-duration-${props.id}`}
      >
        {
          fitnessDurationPath || yogaDurationPath
            ? <Link to={fitnessDurationPath || yogaDurationPath}>
              {formatDuration(props.duration)}
            </Link> : formatDuration(props.duration)
        }
      </span>,
    )
  }
  return meta
}

class TileVideo extends Component {
  onClickVideoTitle = (evt) => {
    const { props } = this
    const {
      id,
      title,
      feature,
      uiLocation,
      mediaLanguage,
      upstreamContext,
      setUpstreamContext,
      setDefaultGa4Event,
    } = props

    // GA4 events
    setDefaultGa4Event(CLICK_TO_DETAILS_PAGE_GA4_EVENT.mergeDeep({
      content_id: id,
      content_name: title,
      content_type: TYPE_CONTENT_VIDEO,
      media_id: feature.get('id'),
      media_language: mediaLanguage,
      is_featured_media: true,
      ui_location: uiLocation,
      ui_element: 'Tile',
    }))

    upstreamContextOnClick(evt, { upstreamContext, setUpstreamContext })
  }

  renderTopMeta = (props) => {
    const { onClickVideoTitle } = this
    const {
      yogaTeacherPath,
      fitnessInstructorPath,
    } = props
    if (props.type !== TYPE_CONTENT_VIDEO && props.host) {
      return (
        <span className="tile-video__host">
          {
            yogaTeacherPath || fitnessInstructorPath
              ? <Link to={yogaTeacherPath || fitnessInstructorPath}>
                {props.host}
              </Link> : props.host
          }
        </span>
      )
    }

    if (props.type === TYPE_CONTENT_VIDEO) {
      return (
        <Link
          to={props.url}
          className="tile-video__title"
          data-element="video-title"
          onClick={onClickVideoTitle}
        >
          {props.title}
        </Link>
      )
    }

    return null
  }

  renderTitle = (props) => {
    const { onClickVideoTitle } = this
    if (props.type !== TYPE_CONTENT_VIDEO) {
      return (
        <Link
          to={props.url}
          className="tile-video__title"
          data-element="video-title"
          onClick={onClickVideoTitle}
        >
          {props.title}
        </Link>
      )
    }

    return null
  }

  render () {
    const {
      props,
      renderTopMeta,
      renderTitle,
    } = this
    const {
      id,
      auth,
      user,
      type,
      title,
      teaser,
      feature,
      asShare,
      preview,
      itemIndex,
      heroLabel,
      staticText,
      forceAccess,
      isInPlaylist,
      onClickWatch,
      addToPlaylist,
      upstreamContext,
      displayMoreInfoButton,
      mediaLanguage,
      parentSeriesId,
      parentSeriesTitle,
      uiLocation,
    } = props
    const itemClass = Array.isArray(props.videoTileClass)
      ? props.videoTileClass.join(' ')
      : props.videoTileClass
    const ga4EventData = fromJS({
      content_id: id,
      content_type: TYPE_CONTENT_VIDEO,
      content_name: title,
      parent_series_id: parentSeriesId,
      parent_series_name: parentSeriesTitle,
      ui_location: uiLocation,
    })
    return (
      <div data-type={type} className={itemClass ? `tile-video ${itemClass}` : 'tile-video'}>
        <WatchAccess
          auth={auth}
          user={user}
          preview={preview}
          feature={feature}
          accessCheck={asShare ? ACCESS_CHECK_SHARE_FEATURE_GEO : ACCESS_CHECK_AUTH_FEATURE_GEO}
        >
          <WatchAccessDenied>
            <NotAvailable
              type={TYPE_NOT_AVAILABLE_OVERLAY}
              message={staticText.getIn(['data', 'notAvailableInRegion'])}
            />
          </WatchAccessDenied>
          <WatchAccessAllowed>
            {renderLabel(props.label)}
          </WatchAccessAllowed>
        </WatchAccess>
        <TileHero
          asShare={asShare}
          forceAccess={forceAccess}
          displayMoreInfoButton={displayMoreInfoButton}
          id={id}
          type={type}
          image={props.image}
          url={props.url}
          hasPlayIcon
          auth={auth}
          heroLabel={heroLabel}
          watchType={WATCH_NODE_TYPE_FEATURE}
          preview={preview}
          feature={feature}
          showMoreInfo={props.showMoreInfo}
          toolTipComponent={props.toolTipComponent}
          active={props.active}
          itemIndex={itemIndex}
          isInPlaylist={isInPlaylist}
          addToPlaylist={addToPlaylist}
          upstreamContext={upstreamContext}
          onClickWatch={onClickWatch}
          title={title}
          mediaLanguage={mediaLanguage}
          parentSeriesId={parentSeriesId}
          parentSeriesTitle={parentSeriesTitle}
          uiLocation={uiLocation}
        />
        {
          !props.onlyShowHero
          && <React.Fragment>
            {renderTileProgressBar(props.userInfo, props.duration)}
            <div className="tile-video__top-meta">
              <Vote
                auth={props.auth}
                vote={props.vote}
                voteDown={props.voteDown}
                voteId={props.voteId}
                size={VOTE_SIZE_SMALL}
                className="vote--right-align vote--small"
                ga4EventData={ga4EventData}
              />
              {renderTopMeta(props)}
            </div>
            {renderTitle(props)}
            <div className="tile-video__bottom-meta">
              <TileVideoMetaBottom {...props} />
            </div>
            {teaser ? <div className="tile-video__teaser">{teaser}</div> : null}
          </React.Fragment>
        }
      </div>
    )
  }
}

TileVideo.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  app: ImmutablePropTypes.map.isRequired,
  active: PropTypes.bool,
  videoTileClass: PropTypes.array,
  type: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  duration: PropTypes.number,
  label: PropTypes.string,
  userInfo: ImmutablePropTypes.map,
  vote: PropTypes.number,
  voteId: PropTypes.number.isRequired,
  url: PropTypes.string.isRequired,
  host: PropTypes.string,
  asShare: PropTypes.bool,
  yogaStyle: PropTypes.string,
  yogaLevel: PropTypes.string,
  yogaLevelPath: PropTypes.string,
  yogaStylePath: PropTypes.string,
  yogaDurationPath: PropTypes.string,
  yogaTeacherPath: PropTypes.string,
  fitnessStyle: PropTypes.string,
  fitnessLevel: PropTypes.string,
  fitnessLevelPath: PropTypes.string,
  fitnessStylePath: PropTypes.string,
  fitnessDurationPath: PropTypes.string,
  fitnessInstructorPath: PropTypes.string,
  meditationStyle: PropTypes.string,
  onlyShowHero: PropTypes.bool,
  showMoreInfo: PropTypes.bool.isRequired,
  toolTipComponent: PropTypes.element,
  preview: ImmutablePropTypes.map.isRequired,
  feature: ImmutablePropTypes.map.isRequired,
  onClickWatch: PropTypes.func,
  staticText: ImmutablePropTypes.map.isRequired,
  isInPlaylist: PropTypes.bool,
  addToPlaylist: PropTypes.bool,
  upstreamContext: ImmutablePropTypes.map,
  heroLabel: PropTypes.string,
  teaser: PropTypes.string,
  forceAccess: PropTypes.oneOf([
    ACCESS_FEATURE,
    ACCESS_PREVIEW,
  ]),
}

TileVideo.defaultProps = {
  vote: 0,
}

export default compose(
  connectRouter(),
  connect(
    (state) => ({
      app: state.app,
      user: state.user,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setUpstreamContext: actions.upstreamContext.setUpstreamContext,
        setDefaultGa4Event: actions.eventTracking.setDefaultGa4Event,
      }
    },
  ),
  connectStaticText({ storeKey: 'tileVideo' }),
)(TileVideo)
